/**
 * Stat strip rendering helper. Merges admin-authored narrative stats with
 * DB-derived platform stats into a capped list of cells for the CIH stat strip.
 *
 * Narrative first, platform second. Cap at 4. If total < 3, render nothing.
 *
 * Aliases: stat strip, cih stats, narrative + platform stats
 */

import type { NarrativeStat } from "~/lib/shared/cih/narrative-stats";

export type StatCell = { value: string; label: string };

/** A derived stat may legitimately be missing (null value) — skipped in output. */
export type DerivedStatCandidate = { value: string | null; label: string };

export type BuildStatStripArgs = {
  narrativeStats: NarrativeStat[] | null;
  hidePlatformStats: boolean;
  derivedStats: DerivedStatCandidate[];
};

const MAX_CELLS = 4;
const MIN_TO_RENDER = 3;

export function buildStatStrip({
  narrativeStats,
  hidePlatformStats,
  derivedStats,
}: BuildStatStripArgs): StatCell[] {
  const narrative: StatCell[] = narrativeStats ?? [];

  const platform: StatCell[] = hidePlatformStats
    ? []
    : derivedStats
        .filter((s): s is StatCell => s.value !== null && s.value !== "")
        .map(({ value, label }) => ({ value, label }));

  const combined = [...narrative, ...platform].slice(0, MAX_CELLS);

  return combined.length >= MIN_TO_RENDER ? combined : [];
}
